import {
  ICoordinateSystemInfoProvider,
  SCENE_FACTORY_TOKEN,
  SceneFactory,
  T_ICoordinateSystemInfoProvider,
} from '@cgs/common';
import {
  PaytablePopupComponent,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  ScrollableProgressBarComponent,
  SLOT_SESSION_TOKEN,
  SlotSession,
  SoundInstance,
} from '@cgs/machines';
import {
  Button,
  Compatibility,
  Container,
  NinePatchSceneObject,
  PIXEL_RATIO,
  Platform,
  ProgressBar,
  SceneObject,
  SoundSceneObject,
  T_Platform,
  TemplateApplication,
  TextSceneObject,
  Vector2,
  View,
} from '@cgs/wge';

import { LOCALIZATION_MANAGER_TOKEN, LocalizationManager } from '@cgs/shared';
import { TrifectaHudZIndex } from './trifecta-hud-z-index';
import {
  TRIFECTA_LAUNCH_PARAMS_TOKEN,
  TrifectaLaunchParamsDto,
} from '../../../models/trifecta-dto';

const enum SceneNames {
  GameRulesPopup = 'gameRulesPopup',
  CloseButton = 'gamerulesPanelBtnClose',
  PopupOrientation = 'popupOrientation',
  BackDropArea = 'gameRulesAreaBtn',
  BackgroundImage = 'bg',
  ClickSound = 'button_click',
}

const enum StateNames {
  ShowPopup = 'gameRulesPopup_show',
  HidePopup = 'gameRulesPopup_hide',
  Default = 'gameRulesPopup_default',
}

export class TrifectaGameRules extends PaytablePopupComponent {
  protected gameRulesPopup: SceneObject;

  protected slotSession: SlotSession;
  protected popupOrientation: SceneObject;
  protected sceneFactory: SceneFactory;

  protected scrollableProgressBar: ScrollableProgressBarComponent;

  protected gameRulesBackgroundOriginalSize: Vector2;
  protected closeButtonOriginalPosition: Vector2;

  protected currency: string;
  protected currencyDecimals: number;

  protected readonly localizationManager: LocalizationManager;
  protected readonly launchParams: TrifectaLaunchParamsDto;

  constructor(container: Container) {
    super();

    this.launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    const resourcesComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    const scene = resourcesComponent.root;

    const soundClickScene = resourcesComponent.sounds
      .findById<SoundSceneObject>(SceneNames.ClickSound)
      ?.findAllByType(SoundSceneObject)[0];

    const soundClick = new SoundInstance(soundClickScene || null);

    this.sceneFactory = container.resolve<SceneFactory>(SCENE_FACTORY_TOKEN);

    const gameRulesScenePopup = this.sceneFactory.build('bottom/sceneGameRules');
    if (!gameRulesScenePopup) {
      throw new Error('game rules scene popup not found');
    }

    const gameRulesPopup = gameRulesScenePopup.findById<SceneObject>(SceneNames.GameRulesPopup);

    if (!gameRulesPopup) {
      throw new Error('game rules popup not found');
    }

    const backDropArea = gameRulesPopup.findById<Button>(SceneNames.BackDropArea);

    if (!backDropArea) {
      throw new Error('back drop area not found');
    }

    const popupOrientation = gameRulesPopup.findById<SceneObject>(SceneNames.PopupOrientation);

    if (!popupOrientation) {
      throw new Error('popup orientation not found');
    }

    this.popupOrientation = popupOrientation;

    const closeButton = gameRulesPopup.findById<Button>(SceneNames.CloseButton);

    if (!gameRulesPopup) {
      throw new Error('game rules popup not found');
    }

    if (!closeButton) {
      throw new Error('close button not found');
    }

    const launchParams = container.resolve<TrifectaLaunchParamsDto>(TRIFECTA_LAUNCH_PARAMS_TOKEN);

    this.currency = launchParams.currencyId;
    this.currencyDecimals = launchParams.currencyDecimals;

    this.localizationManager = container.resolve<LocalizationManager>(LOCALIZATION_MANAGER_TOKEN);

    closeButton.clicked.listen(() => {
      soundClick.stop();
      soundClick.play();
      this.hide();
    });

    backDropArea.clicked.listen(() => {
      soundClick.stop();
      soundClick.play();
      this.hide();
    });

    gameRulesScenePopup.z = TrifectaHudZIndex.GameSettingsPopup;

    scene.addChild(gameRulesScenePopup);

    this.gameRulesPopup = gameRulesPopup;
    this.initGameRules(gameRulesPopup);

    this.slotSession = container.resolve<SlotSession>(SLOT_SESSION_TOKEN);

    this.closeButtonOriginalPosition = closeButton.position.clone();

    gameRulesScenePopup.initialize();

    const gameRulesBackground = gameRulesScenePopup.findById<NinePatchSceneObject>(
      SceneNames.BackgroundImage
    );

    if (!gameRulesBackground) {
      throw new Error('game rules background not found');
    }

    this.gameRulesBackgroundOriginalSize = gameRulesBackground.size.clone();

    const platform = container.resolve<Platform>(T_Platform);

    const coordinateSystem = container.resolve<ICoordinateSystemInfoProvider>(
      T_ICoordinateSystemInfoProvider
    );

    platform.view.sizeChanged.listen(() => {
      this.handleOrientationChange();
      this.updateGameRulesBackgroundSize(
        gameRulesBackground,
        gameRulesPopup,
        coordinateSystem,
        closeButton,
        platform.view
      );
    });

    this.updateGameRulesBackgroundSize(
      gameRulesBackground,
      gameRulesPopup,
      coordinateSystem,
      closeButton,
      platform.view
    );
  }

  public show(): void {
    this.handleOrientationChange();
    this.scrollableProgressBar.resetProgress();
    this.gameRulesPopup.stateMachine?.switchToState(StateNames.ShowPopup);
    this.isShown.emit(true);
  }

  public hide(): void {
    this.gameRulesPopup.stateMachine?.switchToState(StateNames.HidePopup);
    this.isShown.emit(false);
  }

  protected handleOrientationChange(): void {
    this.popupOrientation.stateMachine?.switchToState(
      Compatibility.isPortrait ? 'portrait' : 'landscape'
    );
  }

  protected updateGameRulesBackgroundSize(
    gameRulesBackground: NinePatchSceneObject,
    gameRulesPopup: SceneObject,
    coordinateSystem: ICoordinateSystemInfoProvider,
    closeButton: Button,
    view: View
  ): void {
    gameRulesBackground.size = new Vector2(view.width, view.height).mulNum(PIXEL_RATIO);

    const maskSceneObject = gameRulesPopup.findById<SceneObject>('maskid')!;
    const contentSize = gameRulesPopup.findById<SceneObject>('contentSize')!;
    const rulesHolder = gameRulesPopup.findById<SceneObject>('rulesHolder')!;
    const contentScale = this.scrollableProgressBar.scene.childs[0].scale;

    setTimeout(() => {
      const finalScale = TemplateApplication.scale.clone().multiply(gameRulesPopup.scale);
      const finalSize = coordinateSystem.coordinateSystem.size.clone().divide(finalScale);

      const sizeDiff = finalSize.clone().subtract(this.gameRulesBackgroundOriginalSize);

      gameRulesBackground.size = finalSize;
      const currentSize = maskSceneObject.mask.size.y * maskSceneObject.mask.scale.y;
      const maskSizeDiff = finalSize.y - currentSize - 320;
      maskSceneObject.mask.scale = maskSceneObject.mask.scale
        .clone()
        .multiply(new Vector2(1, 1 + maskSizeDiff / currentSize));

      closeButton.position = this.closeButtonOriginalPosition
        .clone()
        .add(new Vector2(sizeDiff.x, 0));

      const contentWidth = contentSize.bounds.width * contentScale.x;
      const finalSizeWidth = finalSize.x - 100;

      const fitScale = Math.min(finalSizeWidth / contentWidth, 1);

      rulesHolder.scale = new Vector2(fitScale, fitScale);
      const maskHeight =
        maskSceneObject.mask.size.y *
        maskSceneObject.mask.scale.y *
        this.gameRulesPopup.scale.y *
        fitScale *
        0.5;
      this.calculateHeight(this.scrollableProgressBar.scene, maskHeight, this.getMarkerTextId());
    }, 0);
  }

  protected getMarkerTextId(): string {
    return 'price_3_3';
  }

  protected calculateHeight(
    gameRulesScene: ProgressBar,
    holderHeight: number,
    markerTextId: string
  ): void {
    const markerText = gameRulesScene.findById<TextSceneObject>(markerTextId);
    if (!markerText) {
      throw new Error('marker text not found');
    }

    let lastChild = markerText.parent!.childs[0];

    markerText.parent!.childs.forEach((child) => {
      if (child.position.y > lastChild.position.y) {
        lastChild = child;
      }
    });

    const contentHeight = lastChild.position.y + lastChild.size.y;

    this.scrollableProgressBar.scrollableSize = new Vector2(0, -contentHeight + holderHeight);
  }

  protected initGameRules(scene: SceneObject): void {
    const gameRulesScene = this.sceneFactory.build('bottom/sceneGameRulesContent') as ProgressBar;
    const rulesHolder = scene.findById<SceneObject>('rulesHolder');

    if (!rulesHolder) {
      throw new Error('rules holder not found');
    }

    if (!gameRulesScene) {
      throw new Error('game rules scene not found');
    }

    this.scrollableProgressBar = new ScrollableProgressBarComponent(gameRulesScene);

    this.beforeInitGameRules(gameRulesScene);

    this.scrollableProgressBar.initialize();

    rulesHolder.addChild(gameRulesScene);

    this.afterInitGameRules();
  }

  protected afterInitGameRules(): void {}

  protected beforeInitGameRules(_gameRulesScene: SceneObject): void {}
}
